// PLAN-cockpit-v2-iter1 Phase 6: Explore Lens
// facet UI を Sub-sidebar に移動 (Reflect 同様の sessionStorage 橋渡し)
// Header search → sessionStorage 経由で query を受け取る (AppHeader → Explore 自動遷移)
// saved searches は Phase 6 後続で実装、今回は recent queries まで

import { useEffect, useState } from 'react'
import { useV2SearchResults, type V2SearchFilters } from '../hooks/useV2Search'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useViewport } from '../hooks/useViewport'
import { useGlobalSearchQuery } from '../hooks/useGlobalSearchQuery'
import { useSavedSearches, type SavedSearch } from '../hooks/useSavedSearches'
import { FilterBar } from '../components/FilterBar'
import { FacetGroup } from '../components/common/FacetGroup'
import { ResultRow } from '../components/common/ResultRow'
import { EmptyState } from '../components/common/EmptyState'
import type { SearchHit } from '../../types'

const RECENT_KEY = 'cockpit_v2.recent_queries'

function loadRecent(): string[] {
  try { return JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]') } catch { return [] }
}
function pushRecent(q: string) {
  if (!q) return
  const cur = loadRecent()
  const next = [q, ...cur.filter(x => x !== q)].slice(0, 20)
  try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(next)) } catch { /* ignore */ }
}

function readExploreFilters(): Partial<V2SearchFilters> {
  return {
    q: window.sessionStorage.getItem('explore_q') ?? '',
    mode: (window.sessionStorage.getItem('explore_mode') as V2SearchFilters['mode']) ?? 'hybrid',
    domain: window.sessionStorage.getItem('explore_domain') ?? undefined,
    origin: (window.sessionStorage.getItem('explore_origin') as V2SearchFilters['origin']) ?? undefined,
  }
}

function writeExploreFilters(f: Partial<V2SearchFilters>) {
  if (f.q !== undefined) window.sessionStorage.setItem('explore_q', f.q ?? '')
  if (f.mode !== undefined) window.sessionStorage.setItem('explore_mode', f.mode ?? 'hybrid')
  window.sessionStorage.setItem('explore_domain', f.domain ?? '')
  window.sessionStorage.setItem('explore_origin', f.origin ?? '')
  window.dispatchEvent(new CustomEvent('explore_filter_change'))
}

// ===== ExploreFilterContent: デスクトップ Sidebar とモバイル FilterBar 両方で使う =====

interface ExploreFilterContentProps {
  qInput: string
  mode: V2SearchFilters['mode']
  domain: string
  origin: string
  recent: string[]
  savedSearches: SavedSearch[]
  namePrefix?: string  // radio name 衝突防止 (desktop/mobile 同時存在時)
  onQInputChange: (q: string) => void
  onSearch: () => void
  onApply: (q: string, m: V2SearchFilters['mode'], d: string, o: string) => void
  onSaveCurrent: (name: string) => void
  onApplySaved: (s: SavedSearch) => void
  onRemoveSaved: (id: string) => void
}

const pillBase = 'text-xs px-2.5 py-1 rounded-full border transition'
const pillActive = 'bg-accent-primary text-bg-surface border-accent-primary'
const pillIdle = 'border-border-subtle text-text-muted hover:border-border-strong'

export function ExploreFilterContent({
  qInput, mode, domain, origin, recent, savedSearches,
  namePrefix = 'explore',
  onQInputChange, onSearch, onApply,
  onSaveCurrent, onApplySaved, onRemoveSaved,
}: ExploreFilterContentProps) {
  const [saveName, setSaveName] = useState('')
  const [saveOpen, setSaveOpen] = useState(false)

  function handleSave() {
    const name = saveName.trim() || (qInput.trim() || 'Untitled')
    onSaveCurrent(name)
    setSaveName('')
    setSaveOpen(false)
  }

  return (
    <div className="space-y-3 text-sm">
      {/* Search box (常時 visible、折り畳まない) */}
      <div className="space-y-2">
        <input
          type="search"
          value={qInput}
          onChange={e => onQInputChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onSearch() }}
          placeholder="Search… (Enter)"
          className="w-full px-3 py-2 bg-bg-base border border-border-subtle rounded text-sm text-text-base placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
        />
        <div className="flex gap-2">
          <button type="button" onClick={onSearch}
            className="flex-1 px-3 py-1.5 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90">
            Search
          </button>
          <button type="button" onClick={() => setSaveOpen(o => !o)}
            title="Save current search"
            className="px-2 py-1.5 border border-border-subtle rounded text-xs text-text-muted hover:text-accent-primary hover:border-accent-primary">
            ★
          </button>
        </div>
        {saveOpen && (
          <div className="flex gap-2">
            <input
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder={qInput || 'Saved search name'}
              className="flex-1 px-2 py-1 bg-bg-base border border-border-subtle rounded text-xs placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
            />
            <button type="button" onClick={handleSave}
              className="px-2 py-1 bg-accent-primary text-bg-surface rounded text-xs hover:opacity-90">
              Save
            </button>
          </div>
        )}
      </div>

      {/* Facet groups (各 group は折り畳み可能、localStorage 永続化) */}
      <FacetGroup id="explore.mode" label="Mode">
        <div className="flex flex-wrap gap-1.5">
          {(['hybrid', 'fts', 'embedding'] as V2SearchFilters['mode'][]).map(m => (
            <button key={m} type="button"
              onClick={() => onApply(qInput, m, domain, origin)}
              aria-pressed={mode === m}
              data-name={`${namePrefix}-mode`}
              className={`${pillBase} ${mode === m ? pillActive : pillIdle}`}>
              {m}
            </button>
          ))}
        </div>
      </FacetGroup>

      <FacetGroup id="explore.domain" label="Domain">
        <div className="flex flex-wrap gap-1.5">
          {['', 'finance', 'health', 'mind', 'relation', 'science-tech', 'general'].map(d => (
            <button key={d || '_all'} type="button"
              onClick={() => onApply(qInput, mode, d, origin)}
              aria-pressed={domain === d}
              data-name={`${namePrefix}-domain`}
              className={`${pillBase} ${domain === d ? pillActive : pillIdle}`}>
              {d || 'All'}
            </button>
          ))}
        </div>
      </FacetGroup>

      <FacetGroup id="explore.kind" label="Kind">
        <div className="flex flex-wrap gap-1.5">
          {[
            { v: '', label: 'All' },
            { v: 'human', label: 'Entry (human)' },
            { v: 'auto', label: 'Entry (auto)' },
            { v: 'synthesized', label: 'Article' },
          ].map(({ v, label }) => (
            <button key={v || '_all'} type="button"
              onClick={() => onApply(qInput, mode, domain, v)}
              aria-pressed={origin === v}
              data-name={`${namePrefix}-origin`}
              className={`${pillBase} ${origin === v ? pillActive : pillIdle}`}>
              {label}
            </button>
          ))}
        </div>
      </FacetGroup>

      {savedSearches.length > 0 && (
        <FacetGroup id="explore.saved" label="Saved" count={savedSearches.length}>
          <ul className="space-y-1">
            {savedSearches.map(s => (
              <li key={s.id} className="flex items-center gap-2 group">
                <button type="button" onClick={() => onApplySaved(s)}
                  className="flex-1 text-sm text-text-muted hover:text-accent-primary text-left truncate"
                  title={`${s.q || '(empty)'} · ${s.mode}${s.domain ? ' · ' + s.domain : ''}${s.origin ? ' · ' + s.origin : ''}`}>
                  ★ {s.name}
                </button>
                <button type="button" onClick={() => onRemoveSaved(s.id)}
                  title="Remove saved search"
                  className="text-xs text-text-subtle opacity-0 group-hover:opacity-100 hover:text-state-error transition">
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </FacetGroup>
      )}

      {recent.length > 0 && (
        <FacetGroup id="explore.recent" label="Recent" count={recent.length} defaultOpen={false}>
          <ul className="space-y-1">
            {recent.slice(0, 8).map(q => (
              <li key={q}>
                <button type="button" onClick={() => { onQInputChange(q); onApply(q, mode, domain, origin) }}
                  className="text-sm text-text-muted hover:text-accent-primary text-left truncate w-full">
                  {q}
                </button>
              </li>
            ))}
          </ul>
        </FacetGroup>
      )}
    </div>
  )
}

// ===== ExploreSidebar: デスクトップ用 =====

export function ExploreSidebar() {
  const initial = readExploreFilters()
  const [qInput, setQInput] = useGlobalSearchQuery()  // Header と同期
  const [mode, setMode] = useState<V2SearchFilters['mode']>(initial.mode ?? 'hybrid')
  const [domain, setDomain] = useState(initial.domain ?? '')
  const [origin, setOrigin] = useState(initial.origin ?? '')
  const { items: savedSearches, save: saveSearch, remove: removeSaved } = useSavedSearches()

  const applyAndDispatch = (q: string, m: V2SearchFilters['mode'], d: string, o: string) => {
    setMode(m); setDomain(d); setOrigin(o)
    setQInput(q)
    if (q) pushRecent(q)
    writeExploreFilters({ q, mode: m, domain: d || undefined, origin: (o || undefined) as V2SearchFilters['origin'] })
  }

  const handleSaveCurrent = (name: string) => {
    saveSearch({ name, q: qInput, mode: mode ?? 'hybrid', domain, origin })
  }

  const handleApplySaved = (s: SavedSearch) => {
    applyAndDispatch(s.q, s.mode, s.domain, s.origin)
  }

  return (
    <div className="p-4">
      <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Search & Filters</h2>
      <ExploreFilterContent
        qInput={qInput} mode={mode} domain={domain} origin={origin}
        recent={loadRecent()}
        savedSearches={savedSearches}
        onQInputChange={setQInput}
        onSearch={() => applyAndDispatch(qInput, mode, domain, origin)}
        onApply={applyAndDispatch}
        onSaveCurrent={handleSaveCurrent}
        onApplySaved={handleApplySaved}
        onRemoveSaved={removeSaved}
      />
    </div>
  )
}

export function ExploreLens() {
  const { selectedItem, setSelectedItem } = useSelectedItem()
  const viewport = useViewport()
  const initial = readExploreFilters()
  const [filters, setFilters] = useState<V2SearchFilters>({
    q: initial.q ?? '',
    mode: initial.mode ?? 'hybrid',
    domain: initial.domain,
    origin: initial.origin,
    domain_mode: 'broad',
    // q が空のときは sort=-created_at (Newest)、q ありのときは sort=score (relevance)。
    // worker-entry v1 制約: `q + sort != score` は 400、`q 空 + sort != score` は OK。
    sort: (initial.q ?? '').trim() ? 'score' : '-created_at',
    limit: 50,
  })
  const { results, total, loading, error, latencyMs } = useV2SearchResults(filters)

  // モバイル FilterBar: Header と同じ global q を共有
  const [mobileQ, setMobileQ] = useGlobalSearchQuery()
  const [mobileMode, setMobileMode] = useState<V2SearchFilters['mode']>(initial.mode ?? 'hybrid')
  const [mobileDomain, setMobileDomain] = useState(initial.domain ?? '')
  const [mobileOrigin, setMobileOrigin] = useState(initial.origin ?? '')
  const [filterOpen, setFilterOpen] = useState(false)
  const { items: mobileSavedSearches, save: mobileSaveSearch, remove: mobileRemoveSaved } = useSavedSearches()

  const mobileApply = (q: string, m: V2SearchFilters['mode'], d: string, o: string) => {
    setMobileMode(m); setMobileDomain(d); setMobileOrigin(o)
    setMobileQ(q)
    if (q) pushRecent(q)
    writeExploreFilters({ q, mode: m, domain: d || undefined, origin: (o || undefined) as V2SearchFilters['origin'] })
    setFilterOpen(false)  // 検索後にフィルターを自動クローズ
  }

  const mobileSaveCurrent = (name: string) => {
    mobileSaveSearch({ name, q: mobileQ, mode: mobileMode ?? 'hybrid', domain: mobileDomain, origin: mobileOrigin })
  }
  const mobileApplySaved = (s: SavedSearch) => {
    mobileApply(s.q, s.mode, s.domain, s.origin)
  }

  // Sub-sidebar からの filter 変更を受け取る
  useEffect(() => {
    const handler = () => {
      const f = readExploreFilters()
      const q = f.q ?? ''
      // q 有無に応じて sort 切替 (worker-entry v1 制約: q+sort=score 以外は 400)
      setFilters(p => ({ ...p, q, mode: f.mode ?? 'hybrid', domain: f.domain, origin: f.origin, sort: q.trim() ? 'score' : '-created_at' }))
    }
    window.addEventListener('explore_filter_change', handler)
    return () => window.removeEventListener('explore_filter_change', handler)
  }, [])

  // Header からの global query 変更も listen (sidebar が無い状態でも反映)
  useEffect(() => {
    const handler = () => {
      const f = readExploreFilters()
      const q = f.q ?? ''
      setFilters(p => ({ ...p, q, sort: q.trim() ? 'score' : '-created_at' }))
    }
    window.addEventListener('global_q_change', handler)
    return () => window.removeEventListener('global_q_change', handler)
  }, [])

  const highlight = filters.q && filters.mode !== 'embedding' ? filters.q.split(/\s+/).filter(Boolean) : []

  return (
    <div className="h-full flex flex-col">
      {/* モバイル: FilterBar */}
      {viewport === 'mobile' && (
        <FilterBar label="Search & Filters" open={filterOpen} onToggle={() => setFilterOpen(o => !o)}>
          <ExploreFilterContent
            qInput={mobileQ} mode={mobileMode} domain={mobileDomain} origin={mobileOrigin}
            recent={loadRecent()}
            savedSearches={mobileSavedSearches}
            namePrefix="explore-mobile"
            onQInputChange={setMobileQ}
            onSearch={() => mobileApply(mobileQ, mobileMode, mobileDomain, mobileOrigin)}
            onApply={mobileApply}
            onSaveCurrent={mobileSaveCurrent}
            onApplySaved={mobileApplySaved}
            onRemoveSaved={mobileRemoveSaved}
          />
        </FilterBar>
      )}
      {/* 検索中の状態バー */}
      {filters.q && (
        <div className="flex-shrink-0 px-4 py-2 text-xs text-text-subtle border-b border-border-subtle bg-bg-surface flex items-center gap-3">
          <span>Query: <strong className="text-text-base">{filters.q}</strong></span>
          {filters.domain && <span>Domain: <strong className="text-text-base">{filters.domain}</strong></span>}
          {filters.origin && <span>Kind: <strong className="text-text-base">{filters.origin}</strong></span>}
          <span className="ml-auto">{loading ? 'Searching…' : `${total} results${latencyMs != null ? ` in ${latencyMs}ms` : ''}`}</span>
        </div>
      )}

      {/* Result list */}
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <EmptyState title="Search failed" description={error} />
        ) : loading ? (
          <div className="p-6 text-sm text-text-subtle">Searching…</div>
        ) : results.length === 0 && filters.q ? (
          <EmptyState
            title="No results"
            description="左の filter を緩めるか、検索語を変えてみてください (mode=embedding も検討)"
          />
        ) : results.length === 0 ? (
          <div className="max-w-xl mx-auto px-8 py-16 text-center space-y-6">
            <h2 className="text-xl font-medium text-text-base">Start exploring</h2>
            <p className="text-sm text-text-muted">
              左の search box に検索語を入力するか、上部の Header search bar から検索できます。
            </p>
            <p className="text-sm text-text-muted">
              Mode: <strong>hybrid</strong> (FTS + Embedding の Reciprocal Rank Fusion)<br/>
              Domain filter で特定分野に絞り込み可能。
            </p>
          </div>
        ) : (
          <ul role="list">
            {results.map((hit: SearchHit) => {
              const isArticle = hit.origin === 'synthesized'
              const itemType: 'entry' | 'article' = isArticle ? 'article' : 'entry'
              return (
                <li key={hit.id}>
                  <ResultRow
                    id={hit.id}
                    title={hit.title}
                    one_line={hit.one_line}
                    origin={hit.origin}
                    subjectivity={hit.subjectivity}
                    source_project={hit.source_project ?? null}
                    domains={hit.domains ?? []}
                    visibility={hit.visibility ?? null}
                    has_decision={hit.has_decision}
                    x_status={hit.x_status ?? null}
                    created_at={hit.created_at}
                    selected={selectedItem?.id === hit.id}
                    highlight={highlight}
                    onClick={() => setSelectedItem({ id: hit.id, itemType })}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
